import { Action } from "redux";

import { IOpenedFormInfo } from "@multiweb/types/multiweb";
import { TypedObject } from "@multiweb/types/react";
import {
    REMOVE_GROUP_MAP_ITEM,
    REMOVE_LABEL_INFO,
    REMOVE_NUMBERING_GROUP,
    REMOVE_OPENED_ITEM_NUMBER,
    SET_GROUP_MAP_ITEM,
    SET_LABEL_INFO,
    SET_NUMBERING_GROUP,
    SET_OPENED_ITEM_NUMBER,
} from "./constants";

export type CustomTitles = TypedObject<string>;

/**
 * Reprezentuje část stavu menu uchovávající data o otevřených položkách v menu.
 */
export interface IMenuOpenedItemsState {
    /** Identifikátor instance aktivní položky */
    readonly openedFormInstanceId: string | null;
    /** Pole ids otevřených položek v pořadí vykreslování (poslední otevřená položka je první) */
    readonly openedFormsIds: readonly string[];
    /** Informace o otevřených položkách indexované podle jejich id */
    readonly openedFormsInfo: TypedObject<IOpenedFormInfo>;
    /** Obsahuje hodnoty custom titles pro otevřené položky */
    readonly customTitles: CustomTitles;
    /** Hodnoty popisků pro otevřené položky. Zohledňují prioritu zdroje popisku. */
    readonly labelValues: LabelValues;
    /** Uchovává údaje o číslování otevřených položek */
    readonly numbering: INumbering;
}

export interface INumbering {
    /** Skupiny číslování */
    readonly groups: NumberingGroups;
    /** Obsahuje pořadová čísla pro otevřené položky */
    readonly numbers: InstanceNumberMap;
    /** Mapuje otevřené instance na klíč skupiny. */
    readonly groupMap: FormInstanceGroupMap;
}

export type InstanceNumberMap = TypedObject<number>;

export type NumberingGroupInfo = {
    /** Čítač otevřených položek v rámci skupiny */
    readonly counter: number;
    /** Indikátor, zda skupina někdy měla více než jednu položku */
    readonly hadMoreMembers: boolean;
};

export type NumberingGroups = TypedObject<NumberingGroupInfo>;

export type LabelInfo = {
    /** Název otevřené instance. Může být již přeložený název, nebo lokalizační klíč.*/
    readonly labelValue: string | undefined;
    /** Indikátor, zda je labelValue lokalizační klíč, nebo výsledný název. */
    readonly isLocKey: boolean;
};

/**
 * Hodnoty popisků pro otevřené položky. Zohledňují prioritu zdroje popisku.
 */
export type LabelValues = TypedObject<LabelInfo>;

/**
 * Mapuje otevřené položky na klíč skupiny.
 */
export type FormInstanceGroupMap = TypedObject<string>;

/**
 * Reprezentuje otevřenou menu položku formuláře. Narozdíl od informací o otevřeném formuláři (IOpenedFormInfo), ze kterého
 * vychází, má povinné menuItemId. Obecně otevřený formulář totiž nemusí mít položku v menu. Tento typ však reprezentuje
 * otevřenou položku z menu, proto menuItemId bude vždy mít.
 */
export type OpenedFormItemInfo = Required<Pick<IOpenedFormInfo, "formInstanceId" | "menuItemId" | "formCode">>;

/**
 * Reprezentuje otevřenou menu položku profilu. Narozdíl od informací o otevřeném formuláři (IOpenedFormInfo), ze kterého
 * vychází, má všechna pole povinná, protože musí obsahovat id profilu i položky menu.
 */
export type OpenedProfileInfo = Required<IOpenedFormInfo>;

export interface ISetLabelInfoAction extends Action<typeof SET_LABEL_INFO> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly labelValue: string | undefined;
        readonly isLocKey: boolean;
    };
}

export class ISetLabelInfoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_LABEL_INFO;
    }
}

export interface IRemoveLabelInfoAction extends Action<typeof REMOVE_LABEL_INFO> {
    readonly payload: {
        readonly formInstanceId: string;
    };
}

export class IRemoveLabelInfoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_LABEL_INFO;
    }
}

export interface ISetGroupMapItemAction extends Action<typeof SET_GROUP_MAP_ITEM> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly groupKey: string;
    };
}

export class ISetGroupMapItemAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_GROUP_MAP_ITEM;
    }
}

export interface IRemoveGroupMapItemAction extends Action<typeof REMOVE_GROUP_MAP_ITEM> {
    readonly payload: {
        readonly formInstanceId: string;
    };
}

export class IRemoveGroupMapItemAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_GROUP_MAP_ITEM;
    }
}

export interface ISetOpenedItemNumberAction extends Action<typeof SET_OPENED_ITEM_NUMBER> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly number: number;
    };
}

export class ISetOpenedItemNumberAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_OPENED_ITEM_NUMBER;
    }
}

export interface IRemoveOpenedItemNumberAction extends Action<typeof REMOVE_OPENED_ITEM_NUMBER> {
    readonly payload: {
        readonly formInstanceId: string;
    };
}

export class IRemoveOpenedItemNumberAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_OPENED_ITEM_NUMBER;
    }
}

export interface ISetNumberingGroupAction extends Action<typeof SET_NUMBERING_GROUP> {
    readonly payload: {
        readonly groupKey: string;
        readonly groupData: NumberingGroupInfo;
    };
}

export class ISetNumberingGroupAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_NUMBERING_GROUP;
    }
}

export interface IRemoveNumberingGroupAction extends Action<typeof REMOVE_NUMBERING_GROUP> {
    readonly payload: {
        readonly groupKey: string;
    };
}

export class IRemoveNumberingGroupAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REMOVE_NUMBERING_GROUP;
    }
}
